import { useEffect, useRef, useState, memo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { GlobalEvents } from '@highlight/ipc'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { LaunchDarklyRenderer } from '@renderer/utils/launchdarkly.renderer'
import { getReminderCreationMethod, REMINDERS_QUERY_KEY, type ReminderApiItem } from '../Sidepanel/hooks/use-reminders'
import { autoResolutionOf } from '../Sidepanel/hooks/reminder-mapping'
import { loadAnnouncedIds, rememberAnnounced, selectFreshAutoResolutions, selectFreshItems } from './new-item-detection'

// Cap on the new-reminder dedupe set so it stays bounded across a long overlay
// session. Current reminders are always retained (FIFO eviction drops only the
// oldest, least-likely-to-reappear ids).
const MAX_SEEN_REMINDER_IDS = 1000

/**
 * Provider that observes the reminders query cache at the overlay level.
 * Emits `reminder:new` events for genuinely new items so the Toaster can show notifications.
 * Does NOT fetch - the single query observer lives in use-reminders.ts.
 */
export const RemindersProvider = memo(({ children }: React.PropsWithChildren) => {
  const queryClient = useQueryClient()
  const isInitialLoadRef = useRef(true)
  const prevIdsRef = useRef(new Set<string>())

  // Subscribe to cache changes instead of maintaining a separate query observer
  const [reminders, setReminders] = useState<Array<ReminderApiItem> | undefined>(() =>
    queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY]),
  )

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] !== REMINDERS_QUERY_KEY) return
      if (event.type === 'updated') {
        setReminders(event.query.state.data as Array<ReminderApiItem> | undefined)
      }
    })
    // Sync initial state in case data was already in cache
    setReminders(queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY]))
    return unsubscribe
  }, [queryClient])

  // Detect new reminders and emit events
  useEffect(() => {
    if (!reminders || !Array.isArray(reminders)) return

    const validItems = reminders.filter((r): r is ReminderApiItem => r && typeof r?.id === 'string')
    const currentIds = new Set(validItems.map((r) => r.id))

    if (isInitialLoadRef.current) {
      // First load - seed prev IDs, no toasts
      isInitialLoadRef.current = false
    } else {
      const toastsEnabled = LaunchDarklyRenderer.get<boolean>('reminders-toasts', false)

      // The list is a capped page (newest 50), so it is not a feed of arrivals:
      // deleting a reminder frees a slot and the refetch backfills an older one
      // this window has never seen. Announce only what is genuinely new — never
      // announced before, not dismissed, and created just now — so a backfill
      // neither toasts nor re-counts as `generated`. See `new-item-detection.ts`.
      const freshItems = selectFreshItems({
        items: validItems,
        seenIds: prevIdsRef.current,
        announcedIds: loadAnnouncedIds('reminder'),
        now: Date.now(),
        isRetired: (item) => item.metadata?.dismissed === 'true',
      })

      for (const item of freshItems) {
        if (toastsEnabled) {
          GlobalEvents.emit('reminder:new', item)
          // Only what actually toasted is recorded, so a reminder the toast hook
          // suppresses (user-created, inline in its chat) can still announce
          // itself later.
          rememberAnnounced('reminder', item.id)
        }
        AmplitudeRenderer.trackEvent('Reminder', {
          action: 'generated',
          reminderId: item.id,
          type: item.type,
          creationMethod: getReminderCreationMethod(item),
          sourceType: item.source?.type,
        })
      }
    }

    // Highlight checking a reminder off is news even on the first load. The
    // user did the work elsewhere and has not seen the list since.
    for (const item of selectFreshAutoResolutions({
      items: validItems,
      resolvedAtOf: (item) => autoResolutionOf(item)?.at ?? null,
      announcedIds: loadAnnouncedIds('autoResolvedReminder'),
      now: Date.now(),
      isRetired: (item) => item.metadata?.dismissed === 'true',
    })) {
      GlobalEvents.emit('reminder:auto-resolved', item)
      rememberAnnounced('autoResolvedReminder', item.id)
      AmplitudeRenderer.trackEvent('Reminder', { action: 'auto resolved', reminderId: item.id })
    }

    // Accumulate seen ids (don't replace) so a reminder that transiently drops
    // out of the cache — an optimistic-removal rollback, or a refetch race —
    // and later reappears is NOT re-toasted and re-counted as new. Bound with
    // FIFO eviction so the set can't grow unbounded across a long session.
    for (const id of currentIds) prevIdsRef.current.add(id)
    if (prevIdsRef.current.size > MAX_SEEN_REMINDER_IDS) {
      prevIdsRef.current = new Set(Array.from(prevIdsRef.current).slice(-MAX_SEEN_REMINDER_IDS))
    }
  }, [reminders])

  return <>{children}</>
})
RemindersProvider.displayName = 'RemindersProvider'
